<script setup lang="ts">
import type { Ref } from "vue"
import type { CreateInvitationConfigArgs, UpdateInvitationConfigArgs } from "@/api"
import type { WelcomeInvitationConfig } from "@/models"
import { useQueryClient } from "@tanstack/vue-query"
import { toTypedSchema } from "@vee-validate/zod"
import { useForm } from "vee-validate"
import { computed, nextTick, ref, watchEffect } from "vue"
import z from "zod"
import { GET_EVENT_VAULT_QUERY_KEY, GET_INVITATION_CONFIG, useCreateInvitationConfig, useGetEventVault, useGetInvitationConfig, useUpdateInvitationConfig, useUpdateInvitationSettings } from "@/api"
import InvitationPreview from "@/components/InvitationPreview/InvitationPreview.vue"
import { Button, ButtonGroup, Icon, Input, Switch, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { convertDateTimezone } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const toast = useToast()
const queryClient = useQueryClient()

const { t, d } = useTranslation()

const eventVaultId = useConvertedRouteParam("id", "/event/[id]/manage-invitation", value => Number(value))

const { data: eventVault, isLoading: isEventLoading } = useGetEventVault(eventVaultId)
const { data: invitation, isLoading: isInvitationLoading } = useGetInvitationConfig(eventVaultId)
const { mutate: createInvitation, isPending: isCreatingInvitation } = useCreateInvitationConfig(eventVaultId)
const { mutate: updateInvitation, isPending: isUpdatingInvitation } = useUpdateInvitationConfig(eventVaultId)
const { mutate: updateInvitationSettings, isPending: isUpdatingInviteSettings } = useUpdateInvitationSettings()

const isInitialLoading = computed(() => isEventLoading.value || isInvitationLoading.value)
const isMutateLoading = computed(() => isCreatingInvitation.value || isUpdatingInvitation.value || isUpdatingInviteSettings.value)

const isRsvpEmailFeatureEnabled = computed(() => {
  const source = invitation.value !== undefined
    ? invitation.value?.rsvpEmailEnabled
    : eventVault.value?.eventVaultSettings?.rsvpEmailEnabled
  return source !== undefined && source !== null
})

const invitationEnabled: Ref<boolean> = ref(false)
const mode = ref<"EDIT" | "PREVIEW">("EDIT")

function handleSettingsUpdate() {
  if (!eventVault.value)
    return

  updateInvitationSettings({ eventVault: eventVault.value, enableInvitation: invitationEnabled.value }, {
    onSuccess: async () => {
      toast.success({ description: t("manage_invitation_page.toast.settings_update_success") })
      await queryClient.invalidateQueries({ queryKey: [GET_EVENT_VAULT_QUERY_KEY] })
      if (invitation.value)
        initialiseFormValues(invitation.value)
    },
    onError: () => {
      toast.error()
    },
  })
}

const validationSchema = toTypedSchema(
  z.object({
    title: z.string().min(1, t("manage_invitation_page.form.title_required")).max(28, t("manage_invitation_page.form.title_max")),
    invitationMessage: z.string().min(1, t("manage_invitation_page.form.invitation_message_required")).max(1000, t("manage_invitation_page.form.invitation_message_max")),
    rsvpEnabled: z.boolean(),
    rsvpEmailEnabled: z.boolean().optional(),
    allowSongRequests: z.boolean().optional(),
    allowDietaryRequirements: z.boolean().optional(),
    allowPublicGuestList: z.boolean(),
  }),
)

const { handleSubmit, values, resetForm, setFieldValue, meta: formMeta } = useForm({
  validationSchema,
  initialValues: {
    title: undefined,
    invitationMessage: undefined,
    rsvpEnabled: true,
    rsvpEmailEnabled: undefined,
    allowSongRequests: undefined,
    allowDietaryRequirements: undefined,
    allowPublicGuestList: false,
  },
})

const onSubmit = handleSubmit((values) => {
  nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }))

  const isNewInvitation = !invitation.value?.id

  const invitationConfig = {
    title: values.title,
    invitationMessage: values.invitationMessage,
    rsvpEnabled: values.rsvpEnabled,
    rsvpEmailEnabled: values.rsvpEmailEnabled,
    allowSongRequests: values.allowSongRequests,
    allowDietaryRequirements: values.allowDietaryRequirements,
    allowPublicGuestList: !values.rsvpEnabled ? false : values.allowPublicGuestList,
  } satisfies UpdateInvitationConfigArgs

  if (isNewInvitation) {
    const createConfig = invitationConfig satisfies CreateInvitationConfigArgs

    createInvitation(createConfig, {
      onSuccess: async () => {
        toast.success({ description: t("manage_invitation_page.toast.invitation_create_success") })
        await queryClient.invalidateQueries({ queryKey: [GET_INVITATION_CONFIG] })
      },
      onError: () => toast.error(),
    })

    return
  }

  updateInvitation(invitationConfig, {
    onSuccess: async () => {
      toast.success({ description: t("manage_invitation_page.toast.invitation_update_success") })
      await queryClient.invalidateQueries({ queryKey: [GET_INVITATION_CONFIG] })

      if (invitation.value) {
        initialiseFormValues(invitation.value)
      }
    },
    onError: () => toast.error(),
  })
})

const isFormInitialised = ref(false)

async function initialiseFormValues(invitationConfig: WelcomeInvitationConfig) {
  resetForm({ values: {
    title: invitationConfig?.title ?? undefined,
    invitationMessage: invitationConfig?.message ?? undefined,
    rsvpEnabled: invitationConfig?.rsvpEnabled ?? false,
    rsvpEmailEnabled: isRsvpEmailFeatureEnabled.value ? invitationConfig?.rsvpEmailEnabled : undefined,
    allowSongRequests: invitationConfig?.allowSongRequests ?? false,
    allowDietaryRequirements: invitationConfig?.allowDietaryRequirements ?? false,
    allowPublicGuestList: invitationConfig?.allowPublicGuestList ?? false,
  } })

  isFormInitialised.value = true
}

watchEffect(() => {
  if (invitation.value && !isFormInitialised.value)
    initialiseFormValues(invitation.value)

  if (eventVault.value?.eventVaultSettings)
    invitationEnabled.value = eventVault.value.eventVaultSettings.invitationEnabled ?? false

  if (!invitation.value && isRsvpEmailFeatureEnabled.value && values.rsvpEmailEnabled === undefined)
    setFieldValue("rsvpEmailEnabled", eventVault.value?.eventVaultSettings?.rsvpEmailEnabled)
})
</script>

<template>
  <SidebarHostEventLayout>
    <div class="mb-8 flex flex-col lg:flex-row lg:justify-between gap-4 lg:gap-20 lg:items-center">
      <div>
        <h1 class="font-heading font-bold text-xl mb-2">
          {{ t("manage_invitation_page.title") }}
        </h1>
        <p class="text-label text-sm md:text-base font-body">
          {{ t("manage_invitation_page.description") }}
        </p>
      </div>

      <div v-if="isInitialLoading" class="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg w-fit">
        <div class="gpx-skeleton h-4 w-4" />
        <div class="gpx-skeleton h-4 w-16" />
        <div class="gpx-skeleton h-6 w-10 rounded-full ml-2" />
      </div>
      <div v-else class="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-lg lg:w-fit w-full justify-between">
        <div class="flex items-center gap-2">
          <Icon icon="SettingsCog" class="w-4 h-4 text-label shrink-0" />
          <span class="font-body text-sm md:text-base font-semibold text-label">{{ t("manage_invitation_page.enable_invitation") }}</span>
        </div>

        <Switch
          v-model="invitationEnabled"
          variant="icon"
          name="invitationEnabled"
          :is-loading="isUpdatingInviteSettings"
          :readonly="isMutateLoading"
          class="ml-2"
          @change="handleSettingsUpdate"
        />
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="isInitialLoading" class="flex flex-col gap-8">
      <div class="flex w-full justify-end">
        <div class="gpx-skeleton h-10 w-full lg:w-80 rounded-l-lg" />
      </div>
      <div class="flex flex-col gap-4">
        <div class="gpx-skeleton h-6 w-48" />
        <div class="gpx-skeleton h-12 w-full" />
        <div class="gpx-skeleton h-32 w-full" />
      </div>
      <div class="gpx-skeleton h-10 w-32" />
    </div>

    <div v-else class="flex flex-col gap-8">
      <ButtonGroup class="w-full lg:w-1/3">
        <Button
          data-test="card_view_button"
          :variant="mode === 'EDIT' ? 'tertiary' : 'primary'"
          :disabled="mode === 'EDIT'"
          class="flex-1"
          @click="mode = 'EDIT'"
        >
          <Icon icon="Edit" />
          <span>{{ t("manage_invitation_page.edit") }}</span>
          <span class="sr-only">{{ t("manage_invitation_page.card_view_sr") }}</span>
        </Button>
        <Button
          data-test="table_view_button"
          :variant="mode === 'PREVIEW' ? 'tertiary' : 'primary'"
          :disabled="mode === 'PREVIEW' || formMeta.dirty"
          class="flex-1"
          @click="mode = 'PREVIEW'"
        >
          <Icon icon="EyeOn" />
          <span>{{ t("manage_invitation_page.preview") }}</span>
          <span class="sr-only">{{ t("manage_invitation_page.table_view_sr") }}</span>
        </Button>
      </ButtonGroup>

      <form v-show="mode === 'EDIT'" class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <!-- Invitation Content Section -->
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-2">
            <Icon icon="Mail" class="w-5 h-5 text-nav-active" />
            <h3 class="font-heading font-bold text-lg">
              {{ t("manage_invitation_page.invitation_content") }}
            </h3>
          </div>
          <Input
            name="title"
            :label="t('manage_invitation_page.invitation_title')"
            :placeholder="t('manage_invitation_page.invitation_title_placeholder')"
            :disabled="!invitationEnabled || isMutateLoading"
          />
          <Textarea
            rows="8"
            name="invitationMessage"
            :label="t('manage_invitation_page.invitation_message')"
            :placeholder="t('manage_invitation_page.invitation_message_placeholder')"
            :disabled="!invitationEnabled || isMutateLoading"
          />
          <Input name="location" :label="t('manage_invitation_page.form.event_location')" :model-value="eventVault?.location?.displayAddress" disabled />
          <Input name="date" :label="eventVault?.timezone ? t('manage_invitation_page.form.event_date_time') : t('manage_invitation_page.form.event_date')" :model-value="eventVault?.timezone ? d(convertDateTimezone(eventVault.eventDate ?? new Date(), eventVault.timezone), 'long') : d(eventVault?.eventDate ?? new Date(), 'short')" disabled />

          <i18n-t tag="p" class="text-sm text-label" keypath="manage_invitation_page.form.location_date_tip" scope="global">
            <template #eventDetailsLink>
              <a class="underline font-semibold" :href="`/event/${eventVault?.id}/edit`" target="_blank">
                {{ t("manage_invitation_page.form.event_details_link") }}
              </a>
            </template>
          </i18n-t>
        </div>

        <!-- RSVP Settings Section -->
        <div class="flex flex-col gap-4 font-body">
          <div class="flex items-center gap-2">
            <Icon icon="SettingsCog" class="w-5 h-5 text-nav-active" />
            <h3 class="font-heading font-bold text-lg">
              {{ t("manage_invitation_page.rsvp_settings") }}
            </h3>
          </div>

          <div class="flex flex-col gap-4 p-4 rounded-lg border border-gray-300">
            <div class="flex items-center justify-between gap-2">
              <div class="flex flex-col">
                <span class="font-body font-semibold">{{ t("manage_invitation_page.rsvp_enabled_label") }}</span>
                <span class="text-sm text-label mt-1">{{ t("manage_invitation_page.rsvp_enabled_description") }}</span>
              </div>
              <Switch
                name="rsvpEnabled"
                variant="icon"
                :disabled="!invitationEnabled || isMutateLoading"
              />
            </div>

            <!-- RSVP Options -->
            <div v-show="values.rsvpEnabled" class="border-l-2">
              <div v-show="isRsvpEmailFeatureEnabled" class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div class="flex flex-col">
                  <span class="font-body font-medium text-sm">{{ t("manage_invitation_page.guest_confirmation_email_label") }}</span>
                  <span class="text-xs text-label mt-0.5">{{ t("manage_invitation_page.guest_confirmation_email_description") }}</span>
                </div>
                <Switch
                  name="rsvpEmailEnabled"
                  variant="icon"
                  :disabled="!invitationEnabled || isMutateLoading"
                />
              </div>

              <div class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div class="flex flex-col">
                  <span class="font-body font-medium text-sm">{{ t("manage_invitation_page.dietary_requirements_label") }}</span>
                  <span class="text-xs text-label mt-0.5">{{ t("manage_invitation_page.dietary_requirements_description") }}</span>
                </div>
                <Switch
                  name="allowDietaryRequirements"
                  variant="icon"
                  :disabled="!invitationEnabled || isMutateLoading"
                />
              </div>

              <div class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div class="flex flex-col">
                  <span class="font-body font-medium text-sm">{{ t("manage_invitation_page.song_requests_label") }}</span>
                  <span class="text-xs text-label mt-0.5">{{ t("manage_invitation_page.song_requests_description") }}</span>
                </div>
                <Switch
                  name="allowSongRequests"
                  variant="icon"
                  :disabled="!invitationEnabled || isMutateLoading"
                />
              </div>

              <div class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div class="flex flex-col">
                  <span class="font-body font-medium text-sm">{{ t("manage_invitation_page.guest_list_public_label") }}</span>
                  <span class="text-xs text-label mt-0.5">{{ t("manage_invitation_page.guest_list_public_description") }}</span>
                </div>
                <Switch
                  name="allowPublicGuestList"
                  variant="icon"
                  :disabled="!invitationEnabled || isMutateLoading"
                />
              </div>
            </div>

            <!-- Link to view responses -->
            <div class="border-t">
              <a
                :href="`/event/${eventVaultId}/rsvp-list`"
                target="_blank"
                class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group mt-2"
              >
                <div class="flex items-center gap-2">
                  <Icon icon="Users" class="w-4 h-4 text-link" />
                  <span class="font-body font-semibold text-sm text-link">{{ t("manage_invitation_page.view_rsvp_responses_link") }}</span>
                </div>
                <Icon icon="ArrowRight" class="w-4 h-4 text-link group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </form>

      <InvitationPreview
        v-if="mode === 'PREVIEW'"
        :event-vault="eventVault"
        :invitation-title="values.title"
        :invitation-description="values.invitationMessage"
        :disabled="!invitationEnabled"
      />

      <div class="flex gap-4 pt-4">
        <Button
          type="submit"
          class="lg:w-fit flex-1 lg:flex-initial px-8"
          :disabled="!invitationEnabled || isMutateLoading"
          @click="onSubmit"
        >
          {{ isCreatingInvitation || isUpdatingInvitation ? t("manage_invitation_page.saving") : t("manage_invitation_page.save_changes") }}
          <Icon v-if="isCreatingInvitation || isUpdatingInvitation" icon="Loading" />
        </Button>
      </div>
    </div>
  </SidebarHostEventLayout>
</template>
